import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, ShieldCheck, UsersRound, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "wouter";

import { api } from "@/api";
import { trackTitle } from "@/features/racing/venue";
import {
  Button,
  countdown,
  HorsePortrait,
  money,
  Skeleton,
  TierStars,
} from "@/components/ui";

import type { StableHorse, StableResponse } from "./types";

type HorseDetail = {
  name: string;
  tier: string;
  starRating: string;
  appearance: Record<string, unknown>;
  bloodline: string;
  archetype: string;
  track: string;
  trackName?: string;
  isRetired: boolean;
  nextRaceId?: string | null;
  nextRaceAt?: string | null;
  career: Record<string, number | string>;
  recentForm: Array<{
    raceId: string | null;
    racedAt: string;
    finishPos: number | null;
    distanceM: number | null;
  }>;
  custody: Array<{ event: string; shareBp: number; at: string }>;
  owners: Array<{ displayName: string; shareBp: number }>;
  assignment: { jockeyName?: string; trainerName?: string } | null;
};

export function HorseDetailDrawer({
  horseId,
  onClose,
  stableRow,
}: {
  horseId: string;
  onClose: () => void;
  stableRow?: StableHorse;
}) {
  const drawerRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  const horse = useQuery({
    queryKey: ["horses", horseId],
    queryFn: () => api.horse(horseId),
  });
  const catalog = useQuery({
    queryKey: ["augments", "catalog"],
    queryFn: api.augmentCatalog,
  });
  const [jockeyId, setJockeyId] = useState("");
  const [trainerId, setTrainerId] = useState("");
  const assign = useMutation({
    mutationFn: () =>
      api.assignAugments({
        horseId,
        jockeyId: jockeyId || null,
        trainerId: trainerId || null,
      }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["horses", horseId] }),
  });
  const buyback = useMutation({
    mutationFn: async () => {
      const stable = (await api.stable()) as StableResponse;
      const share = stable.stable.find((row) => row.horseId === horseId);
      if (!share) throw new Error("Active share not found");
      return api.buybackQuote(share.shareId) as Promise<{
        offerId: string;
        kind?: string;
        quoteCents: number;
      }>;
    },
  });
  const sell = useMutation({
    mutationFn: (offerId: string) => api.buybackExecute(offerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["stable"] });
      void queryClient.invalidateQueries({ queryKey: ["balance"] });
      onClose();
    },
  });

  const data = horse.data as HorseDetail | undefined;
  const catalogData = catalog.data as
    | {
        jockeys: Array<{ jockeyId: string; name: string }>;
        trainers: Array<{ trainerId: string; name: string }>;
      }
    | undefined;

  useEffect(() => {
    const appRoot = document.getElementById("root");
    const rootWasInert = appRoot?.hasAttribute("inert") ?? false;
    appRoot?.setAttribute("inert", "");
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => closeRef.current?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !drawerRef.current) return;
      const focusable = [
        ...drawerRef.current.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], select:not(:disabled), summary, [tabindex]:not([tabindex="-1"])',
        ),
      ];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (!rootWasInert) appRoot?.removeAttribute("inert");
    };
  }, [onClose]);

  const nextRaceId = data?.nextRaceId ?? stableRow?.nextRaceId ?? null;
  const nextRaceAt = data?.nextRaceAt ?? stableRow?.nextRaceAt ?? null;
  const purseCents = Number(data?.career.purseEarningsCents ?? 0);
  const prizeCents = Number(data?.career.prizeEarningsCents ?? 0);
  const yourNetCents =
    stableRow != null
      ? (stableRow.earnedCents ?? 0) - stableRow.acquisitionCostCents
      : null;

  return createPortal(
    <div
      className="drawer-scrim drawer-scrim--visible drawer-scrim--horse"
      onMouseDown={onClose}
      role="presentation"
    >
      <aside
        aria-label="Horse detail"
        aria-modal="true"
        className="horse-detail-drawer"
        onMouseDown={(event) => event.stopPropagation()}
        ref={drawerRef}
        role="dialog"
      >
        <header>
          <strong>Racehorse</strong>
          <button
            aria-label="Close horse detail"
            onClick={onClose}
            ref={closeRef}
            type="button"
          >
            <X aria-hidden />
          </button>
        </header>
        {horse.isPending || !data ? (
          <Skeleton rows={6} variant="table" />
        ) : (
          <>
            <HorsePortrait
              horse={{
                horseId,
                name: data.name,
                tier: data.tier,
                appearance: data.appearance,
              }}
            />
            <div className="horse-detail-drawer__title">
              <h2>{data.name}</h2>
              <TierStars rating={data.starRating} tier={data.tier} />
              <span>
                {data.bloodline} · {data.archetype} · {trackTitle(data)}
              </span>
            </div>

            <div className="horse-next-race" data-testid="horse-next-race">
              <CalendarClock aria-hidden size={16} />
              {data.isRetired ? (
                <span>Retired — this horse no longer races.</span>
              ) : nextRaceAt ? (
                <span>
                  Races automatically — next race in{" "}
                  <strong>{countdown(nextRaceAt, now)}</strong> (
                  {new Date(nextRaceAt).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                  ){" "}
                  {nextRaceId ? (
                    <Link href={`/app/?watch=${nextRaceId}`}>Watch</Link>
                  ) : null}
                </span>
              ) : (
                <span>
                  Races automatically — the next field is drawn from the
                  published schedule. No setup needed.
                </span>
              )}
            </div>

            <div className="horse-detail-stats">
              {["races", "wins", "places", "shows", "points"].map((key) => (
                <div key={key}>
                  <span>{key}</span>
                  <strong>{String(data.career[key] ?? 0)}</strong>
                </div>
              ))}
            </div>
            <p className="horse-earnings-line">
              Career earnings <strong>{money(purseCents + prizeCents)}</strong>{" "}
              <small>
                (purses {money(purseCents)} · prizes {money(prizeCents)})
              </small>
            </p>

            {stableRow ? (
              <section
                className="horse-your-position"
                data-testid="horse-your-position"
              >
                <h3>Your position</h3>
                <dl>
                  <div>
                    <dt>You paid</dt>
                    <dd>{money(stableRow.acquisitionCostCents)}</dd>
                  </div>
                  <div>
                    <dt>You've earned</dt>
                    <dd>{money(stableRow.earnedCents ?? 0)}</dd>
                  </div>
                  {stableRow.quoteCents != null ? (
                    <div>
                      <dt>
                        {stableRow.quoteKind === "window"
                          ? "Sells today for"
                          : "Sells now for"}
                      </dt>
                      <dd>{money(stableRow.quoteCents)}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>Net so far</dt>
                    <dd
                      className={
                        (yourNetCents ?? 0) >= 0
                          ? "pnl pnl--up"
                          : "pnl pnl--down"
                      }
                    >
                      {money(yourNetCents)}
                    </dd>
                  </div>
                </dl>
                <small>
                  Earnings are your paid purse and prize shares for this horse.
                  Net excludes the sell price until you sell.
                </small>
              </section>
            ) : null}

            {data.recentForm.length ? (
              <section className="horse-recent-results">
                <h3>Recent results</h3>
                <ul>
                  {data.recentForm.slice(0, 5).map((run) => (
                    <li key={`${run.raceId}:${run.racedAt}`}>
                      <span
                        className={
                          run.finishPos === 1 ? "finish finish--win" : "finish"
                        }
                      >
                        {run.finishPos ? `P${run.finishPos}` : "—"}
                      </span>
                      <span>
                        {run.distanceM ? `${run.distanceM}m · ` : ""}
                        {new Date(run.racedAt).toLocaleDateString([], {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      {run.raceId ? (
                        <Link href={`/app/?watch=${run.raceId}`}>Replay</Link>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section>
              <h3>Owners</h3>
              {data.owners.map((owner) => (
                <p key={owner.displayName}>
                  {owner.displayName} · {owner.shareBp / 100}%
                </p>
              ))}
              <small>
                Ownership history: {data.custody.length} verified events ·
                latest {data.custody[0]?.event ?? "seeded"}
              </small>
            </section>

            <section className="horse-detail-actions">
              <Button onClick={() => buyback.mutate()} variant="secondary">
                Get sell quote
              </Button>
              <Link href={`/app/marketplace?horse=${horseId}`}>
                List on marketplace
              </Link>
              <Link href="/app/syndicates">
                <UsersRound aria-hidden size={14} /> Syndicates
              </Link>
              <Link href={`/app/verify?horse=${horseId}`}>
                <ShieldCheck aria-hidden size={14} /> Verified ownership history
              </Link>
            </section>
            {buyback.data ? (
              <div className="notice notice--sell">
                <span>
                  {buyback.data.kind === "window"
                    ? "Instant value (today only): "
                    : "Standing bid: "}
                  <strong>{money(Number(buyback.data.quoteCents ?? 0))}</strong>
                </span>
                <Button
                  disabled={sell.isPending}
                  onClick={() => sell.mutate(buyback.data.offerId)}
                >
                  {sell.isPending ? "Selling…" : "Sell now"}
                </Button>
              </div>
            ) : null}
            {buyback.error ? (
              <p className="form-error">{buyback.error.message}</p>
            ) : null}
            {sell.error ? (
              <p className="form-error">{sell.error.message}</p>
            ) : null}

            {!data.isRetired ? (
              <details className="horse-boosts">
                <summary>Boosts (optional)</summary>
                <p>
                  Optional — takes effect from the next race day. Your horse
                  races either way.
                </p>
                <p>
                  Current: {data.assignment?.jockeyName ?? "none"} /{" "}
                  {data.assignment?.trainerName ?? "none"}
                </p>
                <div className="assignment-form">
                  <select
                    aria-label="Jockey"
                    onChange={(event) => setJockeyId(event.target.value)}
                    value={jockeyId}
                  >
                    <option value="">No jockey</option>
                    {catalogData?.jockeys.map((jockey) => (
                      <option key={jockey.jockeyId} value={jockey.jockeyId}>
                        {jockey.name}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="Trainer"
                    onChange={(event) => setTrainerId(event.target.value)}
                    value={trainerId}
                  >
                    <option value="">No trainer</option>
                    {catalogData?.trainers.map((trainer) => (
                      <option key={trainer.trainerId} value={trainer.trainerId}>
                        {trainer.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    disabled={assign.isPending}
                    onClick={() => assign.mutate()}
                  >
                    Apply boosts
                  </Button>
                </div>
                {assign.isSuccess ? (
                  <small>Saved — applies from the next race day.</small>
                ) : null}
              </details>
            ) : null}
          </>
        )}
      </aside>
    </div>,
    document.body,
  );
}
